import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/contexts/AppContext';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Alert } from '@/components/common/Alert';
import NumericKeypad from '@/components/features/shared/NumericKeypad';
import OTPInput from '@/components/features/shared/OTPInput';
import LockerMonitor from '@/components/features/shared/LockerMonitor';
import PaymentMethodSelection from '@/components/features/shared/PaymentMethodSelection';
import PaymentTerminal from '@/components/features/shared/PaymentTerminal';
import { verifyPhone, sendOTP, verifyOTP, getParcelDataByBarcode, confirmShipmentDeposit } from '@/services/api';
import { Package, Loader2, QrCode } from 'lucide-react';
import { STEPS } from '@/constants';

type ParcelData = {
  id: string;
};

const ShipmentDropoff = () => {
  const { t } = useTranslation();
  const {
    step,
    setStep,
    phone,
    setPhone,
    otp,
    setOTP,
    error,
    setError,
    assignedLocker,
    setAssignedLocker,
    barcode,
    setBarcode,
    amount,
    setAmount,
  } = useAppContext();

  const [isProcessingBarcode, setIsProcessingBarcode] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [isLockerClosed, setIsLockerClosed] = useState(false);
  const [parcelData, setParcelData] = useState<ParcelData | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'pos' | null>(null);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);

  // Authentication Methods
  const handlePhoneAuth = async () => {
    try {
      const isValid = await verifyPhone(phone, '');
      if (isValid) {
        await sendOTP(phone);
        setStep(STEPS.DROPOFF_OTP);
      } else {
        setError(t('invalidPhoneError'));
      }
    } catch (error) {
      setError(t('phoneVerificationError'));
    }
  };

  const handleOTPVerification = async () => {
    setIsVerifyingOTP(true);
    try {
      const isValid = await verifyOTP(phone, otp);
      if (isValid) {
        await processBarcode(barcode);
      } else {
        setError(t('invalidOTPError'));
      }
    } catch (error) {
      setError(t('otpVerificationError'));
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const processBarcode = async (code: string) => {
    setIsProcessingBarcode(true);
    try {
      const data = await getParcelDataByBarcode(code);
      setParcelData(data as unknown as ParcelData);
      setAssignedLocker(data.lockerId ?? '');
      setAmount(data.requiredPayment || 0);
      
      if (data.requiredPayment && data.requiredPayment > 0) {
        setStep(STEPS.DROPOFF_PAYMENT);
      } else {
        setStep(STEPS.DROPOFF_DEPOSIT);
      }
    } catch (error) {
      setError(t('barcodeVerificationError'));
    } finally {
      setIsProcessingBarcode(false);
    }
  };

  // Handle barcode input from scanner
  useEffect(() => {
    if (step === STEPS.DROPOFF_BARCODE) {
      const handleKeyPress = async (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          if (barcode.length > 0) {
            await processBarcode(barcode);
          }
        } else if (/^\d$/.test(event.key)) {
          setBarcode(barcode + event.key);
        }
      };

      window.addEventListener('keypress', handleKeyPress);
      return () => {
        window.removeEventListener('keypress', handleKeyPress);
      };
    }
  }, [step, barcode]);

  // Payment handlers
  const handlePaymentMethodSelect = async (method: 'paypal' | 'pos') => {
    setPaymentMethod(method);
    if (method === 'paypal') {
      setStep(STEPS.DROPOFF_PAYPAL);
    } else {
      setStep(STEPS.DROPOFF_POS);
    }
  };

  const handlePaymentComplete = async () => {
    setIsPaymentComplete(true);
    setStep(STEPS.DROPOFF_DEPOSIT);
  };

  // Locker handlers
  const handleLockerStateChange = async (isClosed: boolean) => {
    setIsLockerClosed(isClosed);
    if (isClosed) {
      try {
        if (parcelData) {
          await confirmShipmentDeposit(parcelData.id);
        } else {
          setError(t('parcelDataError'));
        }
        setStep(STEPS.DROPOFF_COMPLETE);
      } catch (error) {
        setError(t('confirmationError'));
      }
    }
  };

  const handleRestart = () => {
    setPhone('');
    setOTP('');
    setBarcode('');
    setParcelData(null);
    setError('');
    setStep(STEPS.INITIAL);
  };

  const renderBarcodeEntry = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-center text-foreground">
        {t('scanBarcode')}
      </h2>
      <div className="space-y-4">
        <Input
          type="text"
          placeholder={t('scanBarcodeOrEnter')}
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          className="text-center text-xl text-foreground"
        />
        <NumericKeypad
          onKeyPress={(key) => setBarcode(barcode + key)}
          onClear={() => setBarcode('')}
          onBackspace={() => setBarcode(barcode.slice(0, -1))}
        />
        <Button 
          onClick={() => processBarcode(barcode)} 
          disabled={isProcessingBarcode}
          className="w-full"
        >
          {isProcessingBarcode ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <QrCode className="h-4 w-4 mr-2" />
          )}
          {t('verifyBarcode')}
        </Button>
      </div>
    </div>
  );

  const renderPhoneEntry = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-center text-foreground">
        {t('enterPhoneNumber')}
      </h2>
      <Input
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder={t('phoneNumberPlaceholder')}
      />
      <NumericKeypad
        onKeyPress={(key) => setPhone(phone + key)}
        onClear={() => setPhone('')}
        onBackspace={() => setPhone(phone.slice(0, -1))}
      />
      <Button onClick={handlePhoneAuth} className="w-full">
        {t('continue')}
      </Button>
    </div>
  );

  const renderOTPEntry = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-center text-foreground">
        {t('enterOTP')}
      </h2>
      <OTPInput value={otp} onChange={setOTP} />
      <NumericKeypad
        onKeyPress={(key) => setOTP(otp + key)}
        onClear={() => setOTP('')}
        onBackspace={() => setOTP(otp.slice(0, -1))}
      />
      <Button 
        onClick={handleOTPVerification} 
        disabled={isVerifyingOTP}
        className="w-full"
      >
        {t('verify')}
      </Button>
    </div>
  );

  const renderPaymentSelection = () => (
    <PaymentMethodSelection 
      amount={amount}
      onPaymentMethodSelected={handlePaymentMethodSelect}
      isLoading={false}
    />
  );

  const renderPosPayment = () => (
    <PaymentTerminal 
      amount={amount}
      onPaymentComplete={handlePaymentComplete}
    />
  );

  const renderDeposit = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-center text-foreground">
        {t('depositParcel')}
      </h2>
      <div className="text-center">
        <p className="text-lg font-bold">{t('assignedLocker')}</p>
        <p className="text-3xl font-bold text-primary">{assignedLocker}</p>
      </div>
      <LockerMonitor
        lockerId={assignedLocker}
        onStateChange={handleLockerStateChange}
      />
    </div>
  );

  const renderComplete = () => (
    <div className="space-y-4 text-center">
      <div className="flex justify-center">
        <Package className="h-16 w-16 text-primary" />
      </div>
      <h2 className="text-xl font-bold text-foreground">
        {t('dropoffComplete')}
      </h2>
      <p className="text-sm text-muted-foreground">
        {t('courierNotified')}
      </p>
      <Button onClick={handleRestart} className="w-full">
        {t('finish')}
      </Button>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case STEPS.DROPOFF_START:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-center text-foreground">
              {t('authenticateDropoff')}
            </h2>
            <Button
              onClick={() => setStep(STEPS.DROPOFF_PHONE)}
              variant="outline"
              size="lg"
              className="w-full"
            >
              {t('usePhoneAuth')}
            </Button>
            <Button
              onClick={() => setStep(STEPS.DROPOFF_BARCODE)}
              variant="outline"
              size="lg"
              className="w-full"
            >
              {t('useBarcodeAuth')}
            </Button>
          </div>
        );
      case STEPS.DROPOFF_PHONE:
        return renderPhoneEntry();
      case STEPS.DROPOFF_OTP:
        return renderOTPEntry();
      case STEPS.DROPOFF_BARCODE:
        return renderBarcodeEntry();
      case STEPS.DROPOFF_PAYMENT:
        return renderPaymentSelection();
      case STEPS.DROPOFF_POS:
        return renderPosPayment();
      case STEPS.DROPOFF_DEPOSIT:
        return renderDeposit();
      case STEPS.DROPOFF_COMPLETE:
        return renderComplete();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {error && <Alert variant="destructive">{error}</Alert>}
      {renderStep()}
      {step !== STEPS.DROPOFF_COMPLETE && (
        <Button onClick={handleRestart} variant="outline" className="w-full">
          {t('cancel')}
        </Button>
      )}
    </div>
  );
};

export default ShipmentDropoff;